import React, { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { TextField, FormControlLabel, Switch, Grid, MenuItem } from '@mui/material';
import { Tour, Discord, Ruleset } from './TourApi';
import { useTranslation } from 'react-i18next';
import Button from 'components/CustomButtons/Button.js';

const useStyles = makeStyles<Theme>((theme) => ({
  form: {
    width: '100%',
    margin: '0 auto',
    textAlign: 'left',
  },
  formControl: {
    marginBottom: theme.spacing(2),
    width: '100%',
    textAlign: 'left',
  },
  button: {
    width: '100%',
  },
  title: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    textAlign: 'left',
  },
  description: {
    minHeight: '200px',
  },
  switchContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
  },
}));

interface TourFormProps {
  tour?: Tour;
  discords: Discord[];
  rulesets: Ruleset[];
  onSubmit: (tour: Tour) => void;
  onCancel: () => void;
  isLoading: boolean;
}

// Format a date as YYYY-MM-DDThh:mm for the input field
const formatDateForInput = (date: Date): string => {
  return date.toISOString().slice(0, 16);
};

// Format a date as full ISO 8601 with seconds and timezone for API
const formatDateForAPI = (dateString: string): string => {
  // If the dateString is already in full ISO format, return it
  if (dateString.includes('Z') || dateString.includes('+')) {
    return dateString;
  }

  // Otherwise, parse the datetime-local value and add seconds and timezone
  const date = new Date(dateString);
  return date.toISOString(); // Returns format: YYYY-MM-DDThh:mm:ss.sssZ
};

const TourForm: React.FC<TourFormProps> = ({
  tour,
  discords,
  rulesets,
  onSubmit,
  onCancel,
  isLoading,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Tour>({
    name: '',
    ruleset_id: '',
    prize: '',
    description: '',
    date: formatDateForInput(new Date()), // Format for input field
    type: 'one_time',
    published: false,
    discord_id: '',
  });

  useEffect(() => {
    if (tour) {
      setFormData({
        ...tour,
        date: tour.date ? formatDateForInput(new Date(tour.date)) : formatDateForInput(new Date()),
      });
    }
  }, [tour]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Format date properly for API submission
    const submissionData = {
      ...formData,
      date: formatDateForAPI(formData.date),
    };
    onSubmit(submissionData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.checked,
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit} style={{ marginTop: '12px' }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              label={t('tours.form.nameLabel')}
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              disabled={isLoading}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label={t('tours.form.rulesetLabel')}
              name="ruleset_id"
              value={formData.ruleset_id}
              onChange={handleChange}
              fullWidth
              disabled={isLoading}
              SelectProps={{
                MenuProps: {
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                },
              }}
              InputProps={{
                style: { textAlign: 'left' },
              }}
              sx={{
                '& .MuiSelect-select': {
                  textAlign: 'left',
                },
              }}
            >
              {rulesets.map((ruleset) => (
                <MenuItem
                  key={ruleset.id}
                  value={ruleset.id}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  {ruleset.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t('tours.form.prizeLabel')}
              name="prize"
              value={formData.prize}
              onChange={handleChange}
              fullWidth
              disabled={isLoading}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t('tours.form.descriptionLabel')}
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              disabled={isLoading}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t('tours.form.dateLabel')}
              name="date"
              type="datetime-local"
              value={formData.date}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              disabled={isLoading}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label={t('tours.form.typeLabel')}
              name="type"
              value={formData.type}
              onChange={handleChange}
              fullWidth
              disabled={isLoading}
              SelectProps={{
                MenuProps: {
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                },
              }}
              InputProps={{
                style: { textAlign: 'left' },
              }}
              sx={{
                '& .MuiSelect-select': {
                  textAlign: 'left',
                },
              }}
            >
              <MenuItem value="one_time" sx={{ justifyContent: 'flex-start', textAlign: 'left' }}>
                {t('tours.type.oneTime')}
              </MenuItem>
              <MenuItem
                value="recurring_weekly"
                sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
              >
                {t('tours.type.recurringWeekly')}
              </MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label={t('tours.form.discordLabel')}
              name="discord_id"
              value={formData.discord_id}
              onChange={handleChange}
              fullWidth
              disabled={isLoading}
              SelectProps={{
                MenuProps: {
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                },
              }}
              InputProps={{
                style: { textAlign: 'left' },
              }}
              sx={{
                '& .MuiSelect-select': {
                  textAlign: 'left',
                },
              }}
            >
              {discords.map((discord) => (
                <MenuItem
                  key={discord.id}
                  value={discord.id}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  {discord.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <div className={classes.switchContainer}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.published}
                    onChange={handleSwitchChange}
                    name="published"
                    color="primary"
                    disabled={isLoading}
                  />
                }
                label={t('tours.form.publishedLabel')}
              />
            </div>
          </Grid>

          <Grid item xs={12}>
            <Button type="submit" color="primary" disabled={isLoading} fullWidth={true}>
              {tour && tour.id ? t('common.update') : t('common.create')}
            </Button>
            <Button color="outlined" onClick={onCancel} fullWidth={true} disabled={isLoading}>
              {t('common.cancel')}
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default TourForm;
